import { useCallback, useEffect, useState } from "react";
import BenchAdmin from "../components/BenchAdmin";
import { useSetProduct } from "../product";
import { Trans, useTranslation } from "react-i18next";
import Caps from "../components/Caps";
import { useTitle } from "../useTitle";
import { Link, useParams } from "react-router-dom";
import {
  ApiError,
  api,
  atLeast,
  duration,
  ago,
  on,
  roleName,
  type Bench,
  type ConfigField,
  type LeaseEvent,
  type Member,
  type Run,
} from "../api";
import AuditTimeline from "../components/AuditTimeline";
import { MarkdownEditor, MarkdownView } from "../components/Markdown";
import ConfigPanel from "../components/ConfigPanel";
import History from "../components/History";
import Members from "../components/Members";
import VerdictBar from "../components/VerdictBar";
import RunCard from "../components/RunCard";
import { refNeeded } from "../components/Build";
import { Facts, Loading, Notice, PageHead, Views } from "../components/Look";
import { rigState } from "./Now";
import RunPanel from "../components/RunPanel";
import AutoTesting from "../components/AutoTesting";
import i18n from "../i18n";

/**
 * One bench: everything the card carried, plus its own notes document.
 *
 * The card answers "is it alive". This answers "what am I looking at" - the
 * addresses, the quirks, what it is wired to, which of the four passwords it
 * wants. No schema is going to anticipate that, so it is a document.
 */
export default function BenchDetail() {
  const { t } = useTranslation();
  const { id } = useParams();
  const bid = Number(id);
  const [bench, setBench] = useState<Bench | null>(null);
  useSetProduct(bench?.project_id);
  const [runs, setRuns] = useState<Run[]>([]);
  const [leaseLog, setLeaseLog] = useState<LeaseEvent[]>([]);
  const [schema, setSchema] = useState<ConfigField[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [inherited, setInherited] = useState<Member[]>([]);
  const [editing, setEditing] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [tab, setTab] = useState<BenchTab>("runs");
  useTitle(bench ? bench.name : t("benchDetail.fallbackTitle", { id: bid }));

  const load = useCallback(async () => {
    try {
      const d = await api.bench(bid);
      setBench(d.bench);
      setRuns(d.runs);
      setLeaseLog(d.lease_log || []);
      setSchema(d.config_schema || []);
      setMembers(d.members || []);
      setInherited(d.inherited || []);
      setError("");
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    }
  }, [bid]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save(md: string) {
    setBusy(true);
    try {
      await api.updateBench(bid, { notes_md: md });
      setEditing(false);
      await load();
    } catch (e) {
      setError(e instanceof ApiError ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      {error && <Notice tone="error">{error}</Notice>}
      {!bench && !error && <Loading label={t("common.loading")} />}

      {bench && (
        <>
          {/* THE HEAD, AND WHY IT IS A HEAD.

              This page was twelve always-open sections, 3 221 px on a desktop
              and 3 573 px on a phone. The facts you open a bench page FOR - is
              it alive, who has it, what is it, when did it last speak - were at
              about 60% depth, below the access list, under a role glossary
              printed in full on every visit. They are here, above the fold;
              everything else became a tab, and none of it was removed. */}
          <PageHead
            back={{ to: "/benches", label: t("benchDetail.back") }}
            title={
              <span className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="font-semibold text-slate-900 dark:text-slate-100">{bench.name}</span>
                <State bench={bench} />
                {!bench.registered && (
                  <span
                    title={t("benches.noTokenTitle")}
                    className="rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-900 dark:bg-amber-900/40 dark:text-amber-200"
                  >
                    {t("benches.noTokenBadge")}
                  </span>
                )}
                {bench.project_name && (
                  <Link
                    to={`/projects/${bench.project_id}`}
                    className="rounded bg-slate-100 px-1.5 py-0.5 text-xs text-slate-700 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300"
                  >
                    {bench.project_name}
                  </Link>
                )}
                {/* What YOU may do here, said once at the top: every panel below
                    behaves differently depending on it, and a page that only
                    shows that by disabling things reads as broken. */}
                {/* SAID WHEN IT COSTS SOMETHING. Every visit used to carry
                    "your role: guest" in the title line, where it competes with
                    the bench's name and answers a question nobody asked. It is
                    worth a line only when the role is what stops somebody from
                    starting a run, so it appears below operator and not above. */}
                {bench.role && !atLeast(bench.role, "operator") && (
                  <span className="text-sm text-slate-500" title={t("benchDetail.yourRoleTitle")}>
                    {t("benchDetail.yourRole", { role: roleName(bench.role) })}
                  </span>
                )}
              </span>
            }
            answer={
              bench.lease
                ? t("benchDetail.answerHeld", { actor: bench.lease.actor })
                : rigState(bench).tone === "up"
                  ? t("benchDetail.answerFree")
                  : `${rigState(bench).text.charAt(0).toUpperCase()}${rigState(bench).text.slice(1)}.`
            }
            actions={
              // How much the tests of THIS bench prove. The Quality page opened
              // on whichever bench reported last, which for anyone coming from
              // the DisplayOS bench was the SNCF one.
              <Link className="btn-quiet" to={`/quality?bench=${bench.id}`}>
                {t("benchDetail.qualityLink")}
              </Link>
            }
            after={
              <>
                {bench.description && <p>{bench.description}</p>}
                {bench.active_run && (
                  <p className="font-medium text-success-700 dark:text-success-400">
                    {t("benchDetail.factRunning")} {bench.active_run}
                  </p>
                )}
                {/* TWO FACTS, NOT FIVE. Somebody opening a bench wants to know
                    whether it is alive and how much it has done. The agent's
                    version, how many capabilities it claims and the day it was
                    created are setup facts, and Setup is one click away with
                    the rest of them. */}
                <div className="mt-2">
                  <Facts
                    items={[
                      [t("benchDetail.factSeen"),
                       bench.last_seen_at ? ago(bench.last_seen_at) : t("benchDetail.never")],
                      [t("benchDetail.factRuns"), String(bench.run_count)],
                    ]}
                  />
                </div>
              </>
            }
          />

          {/* The lease and the action it gates, together. They were 800 px
              apart and they are one decision. */}
          <LeasePanel
            bench={bench}
            log={leaseLog}
            onRelease={async (force) => {
              try {
                await api.releaseLease(bid, force);
                await load();
              } catch (e) {
                setError(e instanceof ApiError ? e.message : String(e));
              }
            }}
          />
          <RunPanel bench={bench} />
          <AutoTesting bench={bench} />

          <div className="mt-8">
            <BenchTabs tab={tab} setTab={setTab} runs={runs.length} caps={bench.caps.length} />
          </div>

          {tab === "runs" && (
            <>
              <History benchId={bench.id} />
              {runs.length === 0 ? (
                <p className="mt-4 text-sm text-slate-500">
                  {t("benchDetail.nothingReported")}
                </p>
              ) : (
                <>
                  {/* One list, with headers. This table had none at all - four
                      unlabelled columns reading "9/7/26 · 3h 19m · 1894 · 4" -
                      while the identical columns on /runs were labelled. */}
                  <div className="mt-4 hidden overflow-hidden rounded-lg border border-slate-200 sm:block dark:border-slate-800">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 text-left text-xs font-medium text-slate-500 dark:bg-slate-800">
                        <tr>
                          <th scope="col" className="px-3 py-2">{t("benchDetail.colRun")}</th>
                          <th scope="col" className="px-3 py-2">{t("benchDetail.colStarted")}</th>
                          <th scope="col" className="px-3 py-2">{t("benchDetail.colTook")}</th>
                          <th scope="col" className="px-3 py-2 text-right">{t("benchDetail.colSteps")}</th>
                          <th scope="col" className="px-3 py-2">{t("benchDetail.colResult")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {runs.map((r) => (
                          <tr key={r.id}
                              className="border-t border-slate-200 dark:border-slate-800">
                            <td className="px-3 py-2 font-medium">
                              <Link className="hover:underline" to={`/runs/${r.id}`}>{r.name}</Link>
                              {r.reason && (
                                <span className="mt-0.5 block text-xs font-normal text-slate-500">{r.reason}</span>
                              )}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                              {on(r.started_at)}
                            </td>
                            <td className="whitespace-nowrap px-3 py-2 text-slate-500">
                              {duration(r.started_at, r.finished_at)}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{r.step_count}</td>
                            <td className="px-3 py-2"><VerdictBar run={r} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <ul className="mt-4 space-y-2 sm:hidden">
                    {runs.map((r) => <RunCard key={r.id} run={r} showRef={refNeeded(runs).has(r.id)} />)}
                  </ul>
                  <p className="mt-2 text-xs text-slate-500">
                    <Link className="underline" to={`/runs?bench=${bench.id}`}>
                      {t("benchDetail.allRuns")}
                    </Link>
                  </p>
                </>
              )}
            </>
          )}

          {tab === "setup" && (
            <>
              {/* The three facts the head used to carry, with the rest of what
                  this bench IS rather than what it is doing. */}
              <div className="mt-4">
                <Facts
                  items={[
                    [t("benchDetail.factAgent"), bench.agent_version || t("common.dash")],
                    [t("benchDetail.factCaps"),
                     bench.caps.length ? String(bench.caps.length) : t("benchDetail.noCapsReported")],
                    [t("benchDetail.factSince"), on(bench.created_at)],
                  ]}
                />
              </div>
              <Caps bench={bench} />
              <ConfigPanel bench={bench} schema={schema} onSaved={load} />
              <BenchAdmin bench={bench} onChanged={load} />
            </>
          )}

          {tab === "access" && (
            <Members
              scope="bench"
              id={bid}
              members={members}
              inherited={inherited}
              myRole={bench.role}
              onChanged={load}
            />
          )}

          {tab === "notes" && (
            <>
              <div className="mt-4 flex items-center gap-3">
                <h2 className="text-base font-semibold text-slate-800 dark:text-slate-200">
                  {t("benchDetail.notes")}
                </h2>
                {/* Notes are configuration of the rig, so maintainer - the same
                    rung the config panel and the token need. A guest reads them. */}
                {!editing && atLeast(bench.role, "maintainer") && (
                  <button
                    className="ml-auto rounded border border-slate-300 px-2 py-1 text-sm hover:bg-slate-100 dark:border-slate-700 dark:hover:bg-slate-800"
                    onClick={() => setEditing(true)}
                  >
                    {t("benchDetail.editNotes")}
                  </button>
                )}
              </div>
              <div className="mt-2">
                {editing ? (
                  <MarkdownEditor
                    label={t("benchDetail.notesLabel", { name: bench.name })}
                    value={bench.notes_md}
                    busy={busy}
                    onSave={save}
                    onCancel={() => setEditing(false)}
                  />
                ) : (
                  <div className="rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-800 dark:bg-slate-900">
                    <MarkdownView
                      md={bench.notes_md}
                      collapsedPx={340}
                      empty={t("benchDetail.noNotes")}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {tab === "timeline" && <AuditTimeline benchId={bid} />}
        </>
      )}
    </section>
  );
}

type BenchTab = "runs" | "setup" | "access" | "notes" | "timeline";

/** The rig's state as a dot AND a word: a colour is not a label, and green
    against grey is the pair that collapses first under deuteranopia. */
function State({ bench }: { bench: Bench }) {
  const st = rigState(bench);
  return (
    <span
      title={st.why}
      className="inline-flex items-center gap-1.5 text-xs text-slate-600 dark:text-slate-400"
    >
      <span
        className={`h-2.5 w-2.5 rounded-full ${
          st.tone === "up" ? "bg-success-500" : "bg-slate-300 dark:bg-slate-600"
        }`}
        aria-hidden="true"
      />
      {st.text}
    </span>
  );
}


/** The five things a rig is, as tabs. Counts where there is one worth knowing
    before opening: how many runs, how many capabilities it claims. */
function BenchTabs({
  tab, setTab, runs, caps,
}: {
  tab: BenchTab;
  setTab: (t: BenchTab) => void;
  runs: number;
  caps: number;
}) {
  const { t } = useTranslation();
  const items: [BenchTab, string][] = [
    ["runs", `${t("benchDetail.tabRuns")} ${runs}`],
    ["setup", caps ? `${t("benchDetail.tabSetup")} ${caps}` : t("benchDetail.tabSetup")],
    ["access", t("benchDetail.tabAccess")],
    ["notes", t("benchDetail.tabNotes")],
    ["timeline", t("benchDetail.tabTimeline")],
  ];
  return <Views items={items} value={tab} onChange={setTab} label={t("benchDetail.tablistLabel")} />;
}

/**
 * Who has the bench, and what they are doing with it.
 *
 * The purpose line is the point. "Held by drv" tells you to go and ask; "held
 * by drv - installing 1.6.154 from the KVM" tells you whether to bother.
 *
 * Releasing somebody else's lease is possible and deliberately awkward: it is a
 * second click behind a warning, and the server records who took it from whom.
 * A takeover is allowed BECAUSE it is audited - without the record, force is
 * just a way to lose somebody's run quietly.
 */
function LeasePanel({
  bench,
  log,
  onRelease,
}: {
  bench: Bench;
  log: LeaseEvent[];
  onRelease: (force: boolean) => void;
}) {
  const { t } = useTranslation();
  const [confirming, setConfirming] = useState(false);
  const l = bench.lease;

  if (!l) {
    // NOTHING WORTH SAYING TWICE. The head already answers "Free and online."
    // for a bench nobody holds; this panel used to repeat it in other words two
    // lines below, which is the page answering itself. What the head does NOT
    // carry is who has had the bench, so that stays, where it already was:
    // behind a click.
    return log.length > 0 ? (
      <div className="mt-4">
        <LeaseHistory log={log} />
      </div>
    ) : null;
  }

  const stale = l.quiet_for > 15 * 60;
  return (
    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm dark:border-amber-900 dark:bg-amber-950/40">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-amber-900 dark:text-amber-200">
          {t("benchDetail.heldBy", { actor: l.actor })}
        </span>
        <span className="text-amber-900/70 dark:text-amber-200/70">
          {l.seconds_left > 0
            ? t("benchDetail.minutesLeft", { count: Math.ceil(l.seconds_left / 60) })
            : t("benchDetail.pastDeadline")}
          {stale && t("benchDetail.quietFor", { count: Math.floor(l.quiet_for / 60) })}
        </span>
        <button
          className="btn-quiet ml-auto"
          onClick={() => setConfirming(true)}
        >
          {t("benchDetail.release")}
        </button>
      </div>
      {l.purpose && (
        <p className="mt-1 text-amber-900/80 dark:text-amber-200/80">{l.purpose}</p>
      )}
      <p className="mt-1 text-xs text-amber-900/60 dark:text-amber-200/60">
        {t("benchDetail.taken", { when: ago(l.acquired_at) })}
      </p>

      {confirming && (
        <div className="mt-3 rounded border border-amber-400 bg-white p-3 dark:border-amber-800 dark:bg-slate-900">
          <p className="text-sm">
            <Trans
              i18nKey={l.purpose ? "benchDetail.confirmUseFor" : "benchDetail.confirmUse"}
              values={{ actor: l.actor, purpose: l.purpose }}
              components={{ b: <b /> }}
            />
          </p>
          <div className="mt-2 flex gap-2">
            <button className="btn-quiet" onClick={() => setConfirming(false)}>
              {t("benchDetail.leaveIt")}
            </button>
            <button
              className="rounded bg-red-600 px-3 py-1 text-sm font-medium text-white"
              onClick={() => {
                setConfirming(false);
                onRelease(true);
              }}
            >
              {t("benchDetail.takeIt")}
            </button>
          </div>
        </div>
      )}
      {log.length > 0 && <LeaseHistory log={log} />}
    </div>
  );
}

function LeaseHistory({ log }: { log: LeaseEvent[] }) {
  const { t } = useTranslation();
  return (
    <details className="mt-2">
      <summary className="cursor-pointer text-xs text-slate-500">
        {t("benchDetail.whoHasHadIt", { count: log.length })}
      </summary>
      <ul className="mt-1 space-y-0.5 text-xs text-slate-500">
        {log.map((e) => (
          <li key={e.id}>
            <span className="font-mono">{on(e.ts)}</span> ·{" "}
            {i18n.t(`audit.event.${e.event}` as "audit.event.taken", {
              defaultValue: e.event,
            })}{" "}
            · <b>{e.actor}</b>
            {e.took_from && t("benchDetail.tookFrom", { actor: e.took_from })}
            {e.purpose && ` — ${e.purpose}`}
          </li>
        ))}
      </ul>
    </details>
  );
}

